import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from "react-router-dom";
import './index.css';
import App from './App';
import './i18n';
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import RouteLoadingShell from './components/common/RouteLoadingShell';
import ErrorBoundary from './components/ErrorBoundary';
import { installAuthInsights } from './utils/authInsights';

// --- DODANY BLOK: Wyciszenie nadgorliwego błędu ResizeObserver (tylko dla DEV) ---
window.addEventListener('error', (e) => {
  if (
    e.message === 'ResizeObserver loop limit exceeded' ||
    e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    e.message === 'Script error.'
  ) {
    e.stopImmediatePropagation();
    // Ukrycie czerwonej nakładki Webpacka
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }
});

// Zabezpieczenie przed rzucaniem [object Object] do konsoli
const originalConsoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
    return;
  }
  originalConsoleError(...args);
};
// ---------------------------------------------------------------------------------

export const msalInstance = new PublicClientApplication(msalConfig);
installAuthInsights(msalInstance);

/**
 * Must finish before any MSAL API (getAllAccounts / acquireToken / …).
 * Start immediately — usually milliseconds (local). Do NOT also await
 * handleRedirectPromise / profile here (that was the LCP hang).
 */
export const msalInitPromise = msalInstance.initialize();

/**
 * Delay createRoot until initialize() settles so the static #lcp-boot-shell
 * in index.html remains the LCP candidate (React would wipe it on first render).
 * ProtectedRoute still awaits auth; public Layout+Landing paint without profile wait.
 */
function mountApp() {
  // SEO prerender shells (generate-seo-prerender) live inside #root for crawlers.
  // Drop any leftover node (incl. older deploys that injected outside #root) before mount.
  document.getElementById('seo-prerender')?.remove();

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<RouteLoadingShell variant="standalone" />}>
            <HelmetProvider>
              <App msalInstance={msalInstance} />
            </HelmetProvider>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );

  // Keep data-aiq-boot through the first paint (smooth landing handoff), then clear
  // so later SPA visits to / get the normal hero motion again.
  requestAnimationFrame(() => {
    window.setTimeout(() => {
      document.documentElement.removeAttribute('data-aiq-boot');
    }, 400);
  });
}

msalInitPromise
  .then(() => {
    mountApp();
  })
  .catch((err) => {
    console.error('MSAL initialize failed:', err);
    // Still mount app — auth will degrade to guest rather than white-crash.
    mountApp();
  });
